import { AsyncPipe, DecimalPipe } from "@angular/common";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import {
  ErrorHandler,
  LOCALE_ID,
  NgModule,
  inject,
  provideAppInitializer,
  provideExperimentalZonelessChangeDetection,
} from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { Router } from "@angular/router";

import {
  TippyDirective,
  popperVariation,
  provideTippyConfig,
  tooltipVariation,
} from "@ngneat/helipopper";
import * as Sentry from "@sentry/angular";
import { TraceService } from "@sentry/angular";
import { QuillModule } from "ngx-quill";
import { ToastrModule } from "ngx-toastr";

import { DefaultRouteGuard, LoggedInGuard, NotLoggedInGuard } from "@shared/guards";
import { ErrorInterceptor, JwtInterceptor } from "@shared/interceptors";
import { AppDatePipe, LocalTimePipe } from "@shared/pipes";
import { AppInitService } from "@shared/services";
import { LocalizationUtils } from "@shared/utils";

import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { AppComponentsModule } from "./app.component.module";

const appPipes = [AppDatePipe, LocalTimePipe];

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    AppRoutingModule,
    AppComponentsModule,
    TippyDirective,
    BrowserModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot({
      maxOpened: 5,
      autoDismiss: false,
      positionClass: "toast-bottom-left",
      closeButton: true,
      timeOut: 5000,
      newestOnTop: false,
      disableTimeOut: "extendedTimeOut",
      extendedTimeOut: 5000,
    }),
    QuillModule.forRoot({
      sanitize: true,
      modules: {
        toolbar: [
          ["bold", "italic", "underline"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link"],
          ["clean"],
        ],
      },
    }),
  ],
  providers: [
    ...appPipes,
    DecimalPipe,
    AsyncPipe,
    { provide: "Window", useValue: window },
    LoggedInGuard,
    NotLoggedInGuard,
    DefaultRouteGuard,
    { provide: LOCALE_ID, useValue: LocalizationUtils.getLocaleForAngularConfig() },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    provideExperimentalZonelessChangeDetection(),
    provideAppInitializer(() => {
      const initializerFn = ((appInitService: AppInitService) => {
        return async (): Promise<void> => await appInitService.init();
      })(inject(AppInitService));

      return initializerFn();
    }),
    provideTippyConfig({
      defaultVariation: "whiteTooltip",
      variations: {
        tooltip: tooltipVariation,
        popper: popperVariation,
        whiteTooltip: {
          ...tooltipVariation,
          theme: "white",
          arrow: true,
        },
        whiteTooltipNoArrow: {
          ...tooltipVariation,
          theme: "white",
          arrow: false,
        },
      },
    }),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        logErrors: true,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(TraceService);
    }),
  ],
})
export class AppModule {}
